import {inject, Injectable} from '@angular/core';
import {map, Observable, shareReplay, zip} from 'rxjs';
import {Router} from '@angular/router';
import {SIDEBAR_SECTION_TOKEN, SidebarSection, SidebarSectionProvider, SidebarSubItem} from '../../../sidebar-section.provider';
import {HasPermissions, PERMISSION_SERVICE} from '../../services/permission/permission.service';
import {CartService} from 'src/app/prudent-plus-shared/services/cart/cart.service';
import {EmployeeService} from 'src/app/prudent-plus-shared/services/employee/employee.service'; // Add this import

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  private _sectionProviders = inject<SidebarSectionProvider[]>(SIDEBAR_SECTION_TOKEN);
  private permissionService = inject<HasPermissions>(PERMISSION_SERVICE);
  private readonly cartService = inject(CartService);
  private readonly employeeService = inject(EmployeeService);

  public isAccessLocked$(): Observable<boolean> {
    return this.cartService.cartState.pipe(
      map(() => {
        const currentEmployee = this.employeeService.currentEmployee;
        const endorsementPeriods = currentEmployee?.endorsementPeriods ?? [];
        const enrolmentOpen = endorsementPeriods.some((period) => period.isOpen);

        if (!enrolmentOpen) {
          return false;
        }

        const hasNotCompletedEnrolmentBySelf = !(this.cartService.getCart().hasCompletedCart &&
          this.cartService.isCartCompletedBySelf);

        return enrolmentOpen && hasNotCompletedEnrolmentBySelf;
      })
    );
  }

  sections$: Observable<SidebarSection[]> = zip(this._sectionProviders
    .map((provider) => provider.provideSidebarSections()))
    .pipe(map((sections: SidebarSection[]) => {
      return sections.flatMap((section) => {
        return {
          icon: section.icon,
          activeIcon: section.activeIcon,
          title: section.title,
          expanded: section.expanded,
          priority: section.priority = section.priority || 0,
          isActive: section.isActive,
          items: section.items
            .filter((item) => this.isVisibleByActiveFlag(item))
            .filter((item) => this.permissionService.hasPermissions(...item.permissions))
            .map((item) => {
              return {
                ...item,
                sidebarSubItem: item.sidebarSubItem ? this.filterSubItems(item.sidebarSubItem) : undefined,
              };
            })
            .filter((item) => !item.sidebarSubItem || item.sidebarSubItem.length > 0),
        };
      })
        .filter((section) => this.isVisibleByActiveFlag(section))
        .filter((section) => {
          return section.items.length > 0;
        })
        .sort((s1, s2) => {
          if (s1.priority === s2.priority) {
            return s1.title.localeCompare(s2.title);
          }
          if (s1.priority === 0) {
            return 1;
          }
          if (s2.priority === 0) {
            return -1;
          }

          return s1.priority - s2.priority;
        });
    }),
    map((sections) => {
      if (sections.length === 1) {
        sections[0].expanded = true;
      }

      return sections;
    }),
    shareReplay());

  private filterSubItems(subItems: SidebarSubItem[]): SidebarSubItem[] {
    return subItems
      .map((subItem) => {
        return {
          ...subItem,
          children: subItem.children ?
            subItem.children
              .filter((child) => this.isVisibleByActiveFlag(child))
              .filter((child) => this.permissionService.hasPermissions(...child.permissions)) :
            undefined,
        };
      })
      .filter((subItem) => this.isVisibleByActiveFlag(subItem) &&
        this.permissionService.hasPermissions(...subItem.permissions) &&
        (!subItem.children || subItem.children.length > 0));
  }

  private isVisibleByActiveFlag(item: {
    isActive?: boolean;
  }): boolean {
    return item.isActive !== false;
  }

  forceExpand = false;

  constructor() {
    const router = inject(Router);

    router.events.subscribe(() => {
      this.forceExpand = false;
    });
  }
}
